import React from "react";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { AppThunkDispatch } from "@/store";
import { useDispatch } from "react-redux";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import S3FileUpload from "@/components/common/inputs/S3FileUpload";
import {
  AcceptedFileTypes,
  SelectedDocument,
} from "@/components/common/inputs/FileUpload";
import { Document } from "@/pages/T4/DocumentMasterTab";

// Get the action handlers for create and update

interface MasterModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "upload";
  initialData?: any;
  onSuccess: () => void;
  parentId?: string | number | null;
  selectedDocument?: SelectedDocument;
  document?: Document;
  isDriveUpload?: boolean;
}

const MasterModal: React.FC<MasterModalProps> = ({
  open,
  onClose,
  mode,
  initialData,
  onSuccess,
  parentId,
  selectedDocument,
  document,
  isDriveUpload = true,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Folder name is required")
      .min(2, "Folder name must be at least 2 characters")
      .max(50, "Folder name must be at most 50 characters"),
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSubmit = async (values: any) => {
    try {
      const { authenticatedCall: createUpdateMaster } = actionsHandler(
        mode === "create" ? "createFolder" : "updateFolder",
      );
      const payload = {
        name: values.name,
        parent_id: parentId,
      };
      const response = await dispatch(
        createUpdateMaster({
          payload,
          method: mode === "create" ? "post" : "put",
          ...(mode === "edit" && { customParams: [initialData?.id + "/"] }),
        }),
      );

      if (response.ok) {
        formik.resetForm();
        toast.success(
          response.data.message ||
            `${mode === "create" ? "Folder created" : "Folder updated"} successfully`,
        );
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    }
  };

  const formik = useFormik({
    initialValues: {
      name: initialData?.name || "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Add this function to handle successful file upload
  const handleFileUploadSuccess = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-w-[39%]">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div></div>
          <h2 className="tw-text-lg font-weight-semibold font-primary-color">
            {mode === "upload"
              ? "File Upload"
              : mode === "create"
                ? "Create Folder"
                : "Rename"}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-500 tw-hover:text-gray-700"
          >
            <img src={CloseIcon} alt="Close Icon" className="tw-w-5 tw-h-5" />
          </button>
        </div>
        {mode === "upload" && (
          <div className="tw-mb-4">
            <S3FileUpload
              label="Upload File"
              folderId={Number(parentId)}
              accept={[
                AcceptedFileTypes.PDF,
                AcceptedFileTypes.DOCX,
                AcceptedFileTypes.XLSX,
                AcceptedFileTypes.PNG,
                AcceptedFileTypes.JPG,
              ]}
              maxFileSize={10 * 1000 * 1000}
              documentTypeId={document?.id}
              id="fileInput-1"
              isUpdate={selectedDocument?.id ? true : false}
              updateId={selectedDocument?.id}
              disableIsNA={true}
              isDriveUpload={isDriveUpload}
              selectedDocument={selectedDocument}
              onUploadComplete={handleFileUploadSuccess}
            />
          </div>
        )}
        {/* Only show the form if not in upload mode */}
        {mode !== "upload" && (
          <form onSubmit={formik.handleSubmit}>
            <div className="tw-space-y-4 tw-mb-6">
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Enter folder name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name && formik.errors.name?.toString()
                }
                margin="normal"
              />
            </div>
            <div className="tw-flex tw-space-x-4">
              <button
                type="button"
                onClick={handleClose}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                {mode === "create" ? "Create" : "Ok"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MasterModal;
